import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * TRETTO - <tretto-variant-refresh>
 * Re-renders its own content when the customer selects another variant.
 * Modeled on product-inventory.js: the variant picker fetches the updated page HTML and
 * resolves the ProductSelectEvent promise with it; we find our counterpart in that HTML
 * (matched by data-tretto-block, which holds the block id) and morph our children.
 *
 * Expected markup:
 *   <tretto-variant-refresh data-tretto-block="{{ block.id }}" data-product-id="{{ closest.product.id }}">
 */
class TrettoVariantRefresh extends HTMLElement {
  /** @type {Element | null} */
  #closestSection = null;

  connectedCallback() {
    this.#closestSection = this.closest('.shopify-section, dialog');
    this.#closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.#closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#closestSection = null;
  }

  /**
   * Handles the product select event by morphing this element with its freshly rendered version.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const blockId = this.dataset.trettoBlock;
        if (!blockId) return;

        const newElement = html.querySelector(`tretto-variant-refresh[data-tretto-block="${CSS.escape(blockId)}"]`);
        if (!newElement) return;

        morph(this, newElement, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[tretto-variant-refresh] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('tretto-variant-refresh')) {
  customElements.define('tretto-variant-refresh', TrettoVariantRefresh);
}
